// Create a project (arc 101 slice 04, #1727) — `POST /v1/repos`, the greenfield
// mint and the front door of idea → shipped product. `apps/dashboard/CLAUDE.md`
// § "the funnel's front door" carries the rules; the two that decide this file:
//
// A 201 IS NOT A SCAFFOLDED REPO. `scaffold_task_id` MAY BE NULL and that is not
// an error — the repo exists on GitHub and its row landed, so a failed dispatch
// degrades with the reason audited rather than failing the call and stranding a
// caller whose retry hits a name they now own (`apps/api/src/deps/greenfield.ts`,
// § Failure posture). Created-and-scaffolding and created-with-no-session are two
// readings here, and neither claims a task that was never minted.
//
// A 404 IS AN ANSWER, not a missing repo: the route is registered only where
// `cfg.project.create` is wired, so absent the port the path does not exist at
// all. Every other refusal is the port's own 422 sentence, printed verbatim.

import { ApiError } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import { SESSION_TOKEN } from './session';

/**
 * The visibility vocabulary, restated from `RepoVisibilitySchema`
 * (`packages/mcp/src/army/project.ts`) because `@developerz/mcp` is not a
 * dashboard dependency and the vocabulary has no `@developerz/domain` home to
 * import from. A vocabulary, never an authorization: the server's `.strict()`
 * body is the arbiter and its 400 is what a fourth value actually reads.
 */
export const PROJECT_VISIBILITIES = ['private', 'public', 'internal'] as const;

export type ProjectVisibility = (typeof PROJECT_VISIBILITIES)[number];

/**
 * What the form collects — four of the five fields `PROJECT_CREATE_FIELDS` takes.
 *
 * `template_version` is deliberately not one of them. It PINS the gold-standard
 * template and already carries the current version as its default, so offering it
 * would restate that default in a browser bundle and let a human pin a build they
 * have no way to evaluate. Pinning is a machine's concern; the MCP verb and the
 * REST body still take it, which is what `<CopyAsCall/>` hands the reader.
 *
 * `account_id` and `dispatched_by` are stamped from the principal and the body is
 * `.strict()` — a caller able to spell either would attribute a repo, and the
 * inference the scaffold spends, to somebody else. So they are absent by type.
 */
export type ProjectCreateInput = {
  /** The slug under the account org. Bounds + charset are the server's. */
  readonly name: string;
  /** Short description, or null for none — absent and null collapse in the port. */
  readonly description: string | null;
  /** Picked, never defaulted here — see `<CreateProjectForm/>`. */
  readonly visibility: ProjectVisibility;
  /** Advisory stack hint, or null to let the box detect it. */
  readonly stack: string | null;
};

/** What `POST /v1/repos` did, as this client can honestly report it. */
export type ProjectCreateOutcome =
  /** 201: the repo exists. `scaffold_task_id` null = no scaffold session was dispatched. */
  | {
      readonly kind: 'created';
      readonly full_name: string;
      readonly scaffold_task_id: string | null;
    }
  /** A 2xx that named no repository — see {@link readCreateOutcome}. */
  | { readonly kind: 'unclear' }
  /** Any non-2xx: `status` frames the copy, `detail` is the server's own sentence. */
  | { readonly kind: 'refused'; readonly status: number; readonly detail: string | null };

/**
 * The create as ONE value — fired by {@link createProject}, printed by
 * `<CopyAsCall/>`. The MCP half is the `project` resource tool's `create` action,
 * so the verb carries the dot and `mcpRequestBody` moves it into `arguments`.
 */
export function projectCreateCall(input: ProjectCreateInput): ApiCall {
  const body = {
    name: input.name,
    description: input.description,
    visibility: input.visibility,
    stack: input.stack,
  };
  return {
    capability: 'project.create',
    method: 'POST',
    path: '/v1/repos',
    body,
    mcp: { kind: 'verb', verb: 'project.create', arguments: body },
  };
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

/**
 * A 2xx names a repo only when it names a REPOSITORY. A 204, a proxy-injected
 * 200 and a body shaped some other way all leave the caller unable to say what
 * was minted, so they answer `unclear` rather than claim a repo that may not
 * exist — the discriminator-not-default-branch rule `readStartOutcome` holds.
 *
 * `scaffold_task_id` folds absent, null and an empty string into ONE null: all
 * three mean no scaffold session to point at, and the result reads the same.
 */
export function readCreateOutcome(body: unknown): ProjectCreateOutcome {
  const fullName = asRecord(asRecord(body)?.['repository'])?.['full_name'];
  if (typeof fullName !== 'string' || fullName === '') return { kind: 'unclear' };
  const task = asRecord(body)?.['scaffold_task_id'];
  return {
    kind: 'created',
    full_name: fullName,
    scaffold_task_id: typeof task === 'string' && task !== '' ? task : null,
  };
}

/**
 * Mint the repo. Never throws: a refusal IS an outcome (a 401 has already
 * latched the session-expiry gate in `apiFetch`), and a dead network has no
 * status, so it answers 0 and reads as the generic line.
 */
export async function createProject(
  input: ProjectCreateInput,
  token: string | null = SESSION_TOKEN,
): Promise<ProjectCreateOutcome> {
  try {
    return readCreateOutcome(await sendApiCall<unknown>(projectCreateCall(input), token));
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'refused', status: err.status, detail: err.detail ?? null };
    }
    return { kind: 'refused', status: 0, detail: null };
  }
}

/**
 * Copy key framing a refusal. Only the statuses whose STATUS is the whole reason
 * are named: a 403 is the scope, a 404 is the DEPLOYMENT (the route is absent
 * where no GitHub App credential backs it — not a missing repo, and not
 * something a retry fixes), a 422 is the port declining a well-formed ask.
 * Everything else — a 400 on the name, a 429, a 5xx, a dead network — is
 * generic, and `<CreateProjectResult/>` prints the API's own `detail` under
 * whichever line it picks.
 */
const REFUSAL_KEYS: Record<number, string> = {
  403: 'repos.create.refused_scope',
  404: 'repos.create.refused_absent',
  422: 'repos.create.refused_rejected',
};

export const createRefusalKey = (status: number): string =>
  REFUSAL_KEYS[status] ?? 'repos.create.refused_other';
